import React, { useState, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import Tooltip, { TooltipConstants } from '@Components/Tooltip';
import PropTypes from 'prop-types';
import useAccessibleHoverTooltip from '@Components/HoverTooltip/useAccessibleHoverTooltip';
import HoverTooltip from '@Components/HoverTooltip';

import './SvgUseAsButton.scss';

// Workaround for Firefox bug with Flyout placed inside foreignObject - flyout content gets clipped
const overrideTransformModifier = (visible) => ({
  name: 'overrideTransform',
  enabled: true,
  phase: 'write',
  fn({ state }) {
    // The trick is to change position based on visibility
    /* eslint-disable no-param-reassign */
    state.styles.popper.bottom = visible ? '2rem' : '0px';
    state.styles.popper.left = visible ? '1rem' : '0px';
    state.styles.popper.top = 'auto';
    state.styles.popper.right = 'auto';
    /* eslint-enable no-param-reassign */

    Object.assign(state.elements.popper.style, state.styles.popper);
  },
});

export default function SvgUseAsButton(props) {
  const { x, y, href, width, height, onClick, className, tabIndex, children, wrapperClassName, title, tooltipId, tooltip } = props;
  const keyHandler = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      onClick(e);
    }
  };
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipRef, setTooltipRef] = useState(null);
  const popperModifiers = useMemo(
    () => [
      // Falls back to top/left instead of transform
      {
        name: 'computeStyles',
        options: { gpuAcceleration: false },
      },
      overrideTransformModifier(tooltipVisible),
    ],
    [tooltipVisible]
  );

  useAccessibleHoverTooltip(tooltipRef, true);

  useEffect(() => {
    Tooltip.rebuild();
  }, []);

  return (
    <>
      <g
        ref={(ref) => {
          setTooltipRef(ref);
        }}
        onClick={onClick}
        onKeyPress={keyHandler}
        className={classnames('svg-use-as-button--button', wrapperClassName)}>
        <rect fill="transparent" height={height} width={width} y={y} x={x} />
        {title && <title>{title}</title>}
        {href && <use className={className} href={href} height={height} width={width} y={y} x={x} />}
        {children}
      </g>
      <foreignObject
        width="20%"
        height="20%"
        className="svg-use-as-button--foreign-object"
        key={tooltipId || TooltipConstants.ids.TreeViewToolbar}
        y={y}
        x={x}
        onClick={onClick}
        onKeyPress={keyHandler}>
        <div xmlns="http://www.w3.org/1999/xhtml" className="svg-hover-tooltip-wrapper">
          <HoverTooltip
            tag="div"
            theme="simple"
            classnames={classnames(['els-btn-link els-btn-lg info-tooltip', className])}
            tooltip={tooltip}
            modifiers={popperModifiers}
            onToggle={setTooltipVisible}>
            <div tabIndex={tabIndex} />
          </HoverTooltip>
        </div>
      </foreignObject>
    </>
  );
}

SvgUseAsButton.propTypes = {
  tooltipId: PropTypes.string,
  tooltip: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
};
